// Polyfill Object.hasOwn for older browsers (Chrome < 93, Safari < 15.4).
// Required because ky uses Object.hasOwn internally.
import type { KyInstance } from 'ky'
import { loadWithChunkRetry } from '@muse-web/router'
import { getBaseUrl, setBaseUrl } from './helpers/baseUrl'
import { removeSessionFromLocalStorage } from './helpers/clientSession'
import { logError } from './helpers/logError'
import { waitForSessionRestore } from './helpers/sessionRestore'
import { setClientHeaders } from './helpers/setClientHeaders'

if (typeof Object.hasOwn !== 'function') {
    Object.hasOwn = (obj: object, prop: PropertyKey): boolean =>
        Object.prototype.hasOwnProperty.call(obj, prop)
}

let kyLoadPromise: Promise<typeof import('ky')> | null = null

const loadKy = () => {
    kyLoadPromise ??= loadWithChunkRetry(() => import(/* webpackChunkName: 'ky' */ 'ky')).catch(
        (error: unknown) => {
            kyLoadPromise = null
            throw error
        },
    )
    return kyLoadPromise
}

// audio-web read the base URL from process.env.NEXT_PUBLIC_API_URL. In the muse stack it comes
// from the backend app-state (app.domains.api) and is set once at boot via configureHttpClient.
let instance: KyInstance | null = null
let rawInstance: KyInstance | null = null

// App-level hook run on a 401 (after the bearer is dropped) so the app can do a full logout — reset
// the session entry + evict query caches. The client can't reach them directly, so the app registers it.
let onUnauthorized: (() => void) | null = null

export const setOnUnauthorized = (handler: (() => void) | null): void => {
    onUnauthorized = handler
}

const getInstance = async (): Promise<KyInstance> => {
    if (!instance) {
        const { default: ky } = await loadKy()
        instance = ky.create({
            prefixUrl: getBaseUrl(),
            hooks: {
                beforeRequest: [waitForSessionRestore, setClientHeaders],
                // Drop the bearer only on 401 (expired/invalid) — never on 403/429/5xx, so a
                // transient error (e.g. a boot `/me`) can't spuriously log the user out. Then hand off
                // to the app-level handler for the full logout (store + query cache) so the UI can't be
                // left half-authed.
                afterResponse: [
                    (_request, _options, response) => {
                        if (response.status === 401) {
                            removeSessionFromLocalStorage()
                            onUnauthorized?.()
                        }
                    },
                ],
                beforeError: [logError],
            },
        })
    }
    return instance
}

const getRawInstance = async (): Promise<KyInstance> => {
    if (!rawInstance) {
        const { default: ky } = await loadKy()
        rawInstance = ky.create({
            hooks: {
                beforeError: [logError],
            },
        })
    }
    return rawInstance
}

type TBoxedCall = { call: unknown }

const chainable = <T extends object>(boxed: Promise<TBoxedCall>) =>
    new Proxy((() => undefined) as unknown as object, {
        get(_target, prop) {
            if (prop === 'then' || prop === 'catch' || prop === 'finally') {
                const settled = boxed.then(({ call }) => call)
                const value = Reflect.get(settled, prop)
                return typeof value === 'function' ? value.bind(settled) : value
            }

            return (...args: unknown[]) =>
                boxed.then(({ call }) => {
                    const member = Reflect.get(call as object, prop)
                    return typeof member === 'function' ? member.apply(call, args) : member
                })
        },
    }) as unknown as T & PromiseLike<Awaited<T>>

const proxyClient = (resolve: () => Promise<KyInstance>): KyInstance =>
    new Proxy((() => undefined) as unknown as KyInstance, {
        get:
            (_target, prop) =>
            (...args: unknown[]) =>
                chainable(
                    resolve().then(client => {
                        const value = Reflect.get(client, prop)
                        return {
                            call: typeof value === 'function' ? value.apply(client, args) : value,
                        }
                    }),
                ),
        apply: (_target, _thisArg, args) =>
            chainable(
                resolve().then(client => ({
                    call: (client as unknown as (...callArgs: unknown[]) => object)(...args),
                })),
            ),
    })

export const httpClient = proxyClient(getInstance)
export const httpRawClient = proxyClient(getRawInstance)

export const configureHttpClient = ({ baseUrl: nextBaseUrl = '' }: { baseUrl?: string } = {}) => {
    setBaseUrl(nextBaseUrl)
    instance = null
}
